import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { medicationsAPI } from '../api/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ChevronLeft, Save, Coffee, Salad, UtensilsCrossed, Moon, Clock } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const EditMedicationScreen = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  const [displayName, setDisplayName] = useState('');
  const [instructionsText, setInstructionsText] = useState('');
  const [frequency, setFrequency] = useState(1);
  const [selectedTimings, setSelectedTimings] = useState([]);

  const timingOptions = [
    { value: 'before_breakfast', label: 'Before Breakfast', icon: Coffee },
    { value: 'breakfast', label: 'After Breakfast', icon: Coffee },
    { value: 'before_lunch', label: 'Before Lunch', icon: Salad },
    { value: 'lunch', label: 'After Lunch', icon: Salad },
    { value: 'before_dinner', label: 'Before Dinner', icon: UtensilsCrossed },
    { value: 'dinner', label: 'After Dinner', icon: UtensilsCrossed },
    { value: 'bed', label: 'Before Sleep', icon: Moon },
    { value: 'prn', label: 'As Needed', icon: Clock },
  ];

  useEffect(() => {
    const loadMedication = async () => {
      try {
        const meds = await medicationsAPI.getAll();
        const med = meds.find(m => m.id === id);
        if (med) {
          setDisplayName(med.display_name || '');
          setInstructionsText(med.instructions_text || '');
          setFrequency(med.frequency_per_day || 1);
          setSelectedTimings(med.routine_slots || [med.routine_slot]);
        } else {
          toast.error('Medication not found');
          navigate('/meds');
        }
      } catch (error) {
        console.error('Failed to load medication:', error);
        toast.error('Failed to load medication');
        navigate('/meds');
      } finally {
        setLoading(false);
      }
    };
    
    loadMedication();
  }, [id, navigate]);

  const toggleTiming = (timing) => {
    if (timing === 'prn') {
      setSelectedTimings(['prn']);
      return;
    }
    if (selectedTimings.includes('prn')) {
      setSelectedTimings([timing]);
      return;
    }
    if (selectedTimings.includes(timing)) {
      setSelectedTimings(selectedTimings.filter(t => t !== timing));
    } else {
      setSelectedTimings([...selectedTimings, timing]);
    }
  };

  const handleSave = async () => {
    if (!displayName.trim()) {
      toast.error('Please enter medication name');
      return;
    }
    if (selectedTimings.length === 0) {
      toast.error('Please select at least one timing');
      return;
    }

    setSaving(true);
    try {
      await medicationsAPI.update(id, {
        display_name: displayName,
        instructions_text: instructionsText || null,
        routine_slots: selectedTimings,
        frequency_per_day: frequency,
      });
      toast.success('Medication updated!');
      navigate('/meds');
    } catch (error) {
      toast.error('Failed to update medication');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-2xl font-medium text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="screen-container bg-gradient-to-b from-green-50 to-white min-h-screen" data-testid="edit-medication-screen">
      <div className="p-6 space-y-6">
        <div className="flex items-center gap-4 mb-6">
          <Button variant="ghost" size="icon" onClick={() => navigate('/meds')} className="h-12 w-12">
            <ChevronLeft className="h-8 w-8" />
          </Button>
          <h1 className="text-3xl font-bold text-gray-900">Edit Medication</h1>
        </div>

        <div className="space-y-6">
          <div className="space-y-2">
            <Label className="text-lg font-semibold">Medication Name *</Label>
            <Input
              data-testid="input-name"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Enter medication name"
              className="h-14 text-lg"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-lg font-semibold">Instructions (optional)</Label>
            <Input
              data-testid="input-instructions"
              value={instructionsText}
              onChange={(e) => setInstructionsText(e.target.value)}
              placeholder="e.g., Take with food"
              className="h-14 text-lg"
            />
          </div>

          <div className="space-y-2">
            <Label className="text-lg font-semibold">How many times per day?</Label>
            <Select value={frequency.toString()} onValueChange={(v) => setFrequency(parseInt(v))}>
              <SelectTrigger className="h-14 text-lg">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4].map((n) => (
                  <SelectItem key={n} value={n.toString()} className="text-lg py-3">
                    {n}x per day
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label className="text-lg font-semibold">When to take?</Label>
            <div className="grid grid-cols-1 gap-3">
              {timingOptions.map((option) => {
                const Icon = option.icon;
                return (
                  <Button
                    key={option.value}
                    data-testid={`timing-${option.value}`}
                    onClick={() => toggleTiming(option.value)}
                    variant={selectedTimings.includes(option.value) ? 'default' : 'outline'}
                    className={`h-14 text-lg font-semibold justify-start ${
                      selectedTimings.includes(option.value) ? 'bg-green-600 hover:bg-green-700 text-white' : ''
                    }`}
                  >
                    <Icon className="mr-3 h-5 w-5" />
                    {selectedTimings.includes(option.value) && '✓ '}
                    {option.label}
                  </Button>
                );
              })}
            </div>
          </div>

          <Button
            data-testid="btn-save"
            onClick={handleSave}
            disabled={saving}
            className="w-full h-16 text-xl font-bold bg-green-600 hover:bg-green-700 mt-8"
          >
            <Save className="mr-3 h-6 w-6" />
            {saving ? 'Saving...' : 'Save Changes'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default EditMedicationScreen;
